import React, { useState, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Fade,
  Grid,
  Chip,
  Tooltip,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import CalculateIcon from '@mui/icons-material/Calculate';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { workoutAnalyzerService, RecognizeWorkoutResponse } from '../../services/workout-analyzer.service';
import { CalendarEventDto } from '@/types/calendar';
import { toastService } from '../../services/toast.service';
import { localNowIso, toLocalIso } from '@/utils/datetime';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { SchedulingBlock } from '../calendar/add-event-modals/SchedulingBlock';
import { eventColors } from '@/styles/tokens';

type WizardStep = 'input' | 'recognized' | 'estimated';

interface QuickWorkoutAddModalV2Props {
  open: boolean;
  onClose: () => void;
  selectedDate: string; // YYYY-MM-DD
  initialScheduledAt?: Date;
  onRefreshDashboard?: () => void;
  clientId?: string;
  /** Якщо передано — модаль відкривається в режимі редагування AI-події */
  eventToEdit?: CalendarEventDto | null;
}

const ACCENT = eventColors.workout.main;
const ACCENT_HOVER = eventColors.workout.dark;

const INTENSITY_LABELS: Record<string, string> = {
  low: 'Низька', medium: 'Середня', high: 'Висока',
};
const INTENSITY_COLORS: Record<string, string> = {
  low: '#4caf50', medium: '#FF9800', high: '#f44336',
};

export const QuickWorkoutAddModalV2: React.FC<QuickWorkoutAddModalV2Props> = ({
  open,
  onClose,
  selectedDate,
  initialScheduledAt,
  onRefreshDashboard,
  clientId,
  eventToEdit,
}) => {
  const isEditMode = !!eventToEdit;

  const [step, setStep] = useState<WizardStep>('input');
  const [inputText, setInputText] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isEstimating, setIsEstimating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [recognizedEvents, setRecognizedEvents] = useState<RecognizeWorkoutResponse[]>([]);
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showDateTimePicker, setShowDateTimePicker] = useState(!!initialScheduledAt);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(initialScheduledAt || null);
  const [isCompleted, setIsCompleted] = useState(false);

  const buildContextDate = () => `${selectedDate}T${localNowIso().slice(11)}`;
  const getScheduledLdt = () =>
    showDateTimePicker && scheduledAt ? toLocalIso(scheduledAt) : buildContextDate();

  React.useEffect(() => {
    if (!open) return;
    if (eventToEdit) {
      const details = eventToEdit.details as any;
      setInputText(details?.rawInput ?? '');
      setSuggestedTitle(eventToEdit.title ?? '');
      const components: RecognizeWorkoutResponse[] = (details?.components ?? []).map((c: any) => ({
        type: c.type ?? 'strength',
        name: c.name ?? '',
        totalAmount: c.totalAmount ?? undefined,
        unit: c.unit ?? undefined,
        intensity: c.intensity ?? undefined,
        caloriesBurn: c.caloriesBurn ?? undefined,
        scheduledLdt: eventToEdit.scheduledLdt ?? localNowIso(),
      }));
      setRecognizedEvents(components);
      if (eventToEdit.scheduledLdt) {
        setScheduledAt(new Date(eventToEdit.scheduledLdt));
        setShowDateTimePicker(true);
      }
      setIsCompleted(eventToEdit.isCompleted ?? false);
      setStep('estimated');
    } else {
      setStep('input');
      setInputText('');
      setRecognizedEvents([]);
      setSuggestedTitle('');
      setEditingIndex(null);
      setShowDateTimePicker(!!initialScheduledAt);
      setScheduledAt(initialScheduledAt || null);
      setIsCompleted(false);
    }
  }, [open, eventToEdit]);

  const handleClose = () => {
    setStep('input');
    setInputText('');
    setRecognizedEvents([]);
    setSuggestedTitle('');
    setIsRecognizing(false);
    setIsEstimating(false);
    setIsSaving(false);
    setEditingIndex(null);
    setShowDateTimePicker(false);
    setScheduledAt(null);
    setIsCompleted(false);
    onClose();
  };

  const handleRecognize = async () => {
    if (!inputText.trim()) return;
    setIsRecognizing(true);
    setEditingIndex(null);
    setSuggestedTitle('');
    try {
      const data = await workoutAnalyzerService.recognize({
        text: inputText,
        contextDate: getScheduledLdt(),
      }, clientId);

      setSuggestedTitle(data.suggestedTitle || '');

      if (!data.items.length) {
        setRecognizedEvents([]);
        toastService.error('Нічого не вдалося розпізнати. Спробуйте уточнити запит.');
        return;
      }

      setRecognizedEvents(data.items);
      setStep('recognized');
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося розпізнати тренування. Спробуйте ще раз.');
    } finally {
      setIsRecognizing(false);
    }
  };

  const handleEstimate = async () => {
    if (!recognizedEvents.length) return;
    setIsEstimating(true);
    try {
      const data = await workoutAnalyzerService.estimateCalories({
        items: recognizedEvents.map(e => ({
          name: e.name || 'Без назви',
          type: e.type,
          totalAmount: Number(e.totalAmount) || 0,
          unit: e.unit || '',
          intensity: e.intensity || 'medium',
        })),
      }, clientId);
      setRecognizedEvents(
        recognizedEvents.map((e, i) =>
          data[i]?.caloriesBurn ? { ...e, caloriesBurn: data[i].caloriesBurn } : e,
        ),
      );
      setStep('estimated');
      toastService.success('Калорії успішно розраховано');
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося розрахувати калорії.');
    } finally {
      setIsEstimating(false);
    }
  };

  const handleSave = async () => {
    const scheduledLdt = getScheduledLdt();
    const finalIsCompleted = showDateTimePicker ? isCompleted : true;
    const successMsg = isEditMode ? 'Тренування оновлено' : 'Тренування успішно додано';

    const callApi = (payload: Parameters<typeof workoutAnalyzerService.createEventsComplex>[0]) =>
      isEditMode && eventToEdit
        ? workoutAnalyzerService.updateEventsComplex(eventToEdit.id, payload)
        : workoutAnalyzerService.createEventsComplex(payload, clientId);

    setIsSaving(true);
    try {
      if (step === 'input') {
        try {
          await callApi({ startFromStep: 1, text: inputText, scheduledLdt, rawInput: inputText, isCompleted: finalIsCompleted });
          toastService.success(successMsg);
          onRefreshDashboard?.();
          handleClose();
        } catch (error: any) {
          const errBody = error?.response?.data;
          const partial = errBody?.data;
          if (partial?.completedStep >= 1 && partial?.items?.length) {
            const fallbackLdt = partial.scheduledLdt || localNowIso();
            setRecognizedEvents(partial.items.map((item: any) => ({ ...item, scheduledLdt: item.scheduledLdt ?? fallbackLdt })));
            setSuggestedTitle(partial.suggestedTitle || '');
            setStep(partial.completedStep >= 2 ? 'estimated' : 'recognized');
          }
          const validationErrors = errBody?.errors;
          if (validationErrors?.length) {
            toastService.error(validationErrors.map((e: any) => e.message).join('\n'));
          } else {
            toastService.error(errBody?.message || 'Не вдалося зберегти. Спробуйте ще раз.');
          }
        }
        return;
      }

      await callApi({
        startFromStep: step === 'recognized' ? 2 : 3,
        items: recognizedEvents,
        scheduledLdt,
        rawInput: inputText || undefined,
        title: suggestedTitle || undefined,
        isCompleted: finalIsCompleted,
      });
      toastService.success(successMsg);
      onRefreshDashboard?.();
      handleClose();
    } catch (error: any) {
      const errBody = error?.response?.data;
      const validationErrors = errBody?.errors;
      if (validationErrors?.length) {
        toastService.error(validationErrors.map((e: any) => e.message).join('\n'));
      } else {
        toastService.error(errBody?.message || 'Не вдалося зберегти. Спробуйте ще раз.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const totalCalories = useMemo(
    () => recognizedEvents.reduce((acc, e) => acc + (e.caloriesBurn?.perUnit ?? 0) * (Number(e.totalAmount) || 0), 0),
    [recognizedEvents],
  );

  const handleAddItemManual = () => {
    const newItem: RecognizeWorkoutResponse = {
      type: 'strength',
      name: '',
      totalAmount: 0,
      unit: '',
      intensity: 'medium',
      scheduledLdt: localNowIso(),
    };
    const newList = [...recognizedEvents, newItem];
    setRecognizedEvents(newList);
    setEditingIndex(newList.length - 1);
    if (step === 'input') setStep('recognized');
  };

  const handleDeleteItem = (index: number) => {
    const updated = recognizedEvents.filter((_, i) => i !== index);
    setRecognizedEvents(updated);
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
    if (!updated.length) setStep('input');
  };

  const handleUpdateEventField = (index: number, field: keyof RecognizeWorkoutResponse, value: any) => {
    const updated = [...recognizedEvents];
    const parsed = field === 'totalAmount' ? (value === '' ? 0 : Number(value)) : value;
    updated[index] = { ...updated[index], [field]: parsed };
    setRecognizedEvents(updated);
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'strength':   return <FitnessCenterIcon sx={{ color: '#FF9800' }} />;
      case 'cardio':     return <DirectionsRunIcon sx={{ color: '#2196F3' }} />;
      case 'stretching': return <SelfImprovementIcon sx={{ color: '#9C27B0' }} />;
      default:           return <QuestionMarkIcon sx={{ color: '#A8A8A8' }} />;
    }
  };

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const busy = isRecognizing || isEstimating || isSaving;

  // ─── DialogActions ────────────────────────────────────────────────────────
  // dialogActions — однакова логіка для create і edit; handleSave розрізняє всередині
  const dialogActions = step === 'input' ? (
    <>
      <Button
        onClick={handleRecognize}
        variant="outlined"
        disabled={busy || !inputText.trim()}
        startIcon={isRecognizing ? <CircularProgress size={18} color="inherit" /> : <AutoFixHighIcon />}
        sx={{ borderRadius: '10px', px: 3, color: ACCENT, borderColor: ACCENT }}
      >
        {isRecognizing ? 'Розпізнаємо...' : 'Розпізнати'}
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        disabled={busy || !inputText.trim()}
        startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        sx={{ backgroundColor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { backgroundColor: ACCENT_HOVER } }}
      >
        {isSaving ? 'Зберігаємо...' : 'Зберегти'}
      </Button>
    </>
  ) : step === 'recognized' ? (
    <>
      <Button
        onClick={handleEstimate}
        variant="outlined"
        disabled={busy || editingIndex !== null}
        startIcon={isEstimating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
        sx={{ borderRadius: '10px', px: 3, color: ACCENT, borderColor: ACCENT }}
      >
        {isEstimating ? 'Рахуємо...' : 'Розрахувати калорії'}
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        disabled={busy || !recognizedEvents.length || editingIndex !== null}
        startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
        sx={{ backgroundColor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { backgroundColor: ACCENT_HOVER } }}
      >
        {isSaving ? 'Зберігаємо...' : 'Зберегти'}
      </Button>
    </>
  ) : (
    <Button
      onClick={handleSave}
      variant="contained"
      disabled={busy || !recognizedEvents.length || editingIndex !== null}
      startIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
      sx={{ backgroundColor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { backgroundColor: ACCENT_HOVER } }}
    >
      {isSaving ? 'Зберігаємо...' : 'Зберегти'}
    </Button>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px', borderRadius: '16px' } }}
      data-i-doc={ModalIndices.QUICK_WORKOUT_ADD}
    >
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <FitnessCenterIcon sx={{ color: ACCENT }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isEditMode ? 'Редагувати тренування' : 'Додати тренування'}
          </Typography>
        </Box>
        {(!isEditMode || step !== 'estimated') && (
          <Stepper
            activeStep={step === 'input' ? 0 : step === 'recognized' ? 1 : 2}
            sx={{ flex: 1, '& .MuiStep-root': { px: 1 }, '& .MuiStepLabel-root': { py: 0 } }}
          >
            {[0, 1, 2].map((i) => <Step key={i}><StepLabel /></Step>)}
          </Stepper>
        )}
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500], flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ─── Content ────────────────────────────────────────────────────── */}
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', bgcolor: 'action.hover', gap: 2 }}>

        <TextField
          inputRef={inputRef}
          fullWidth
          label="Опишіть ваше тренування..."
          placeholder="Наприклад: 10 віджимань, 15 присідань, розминка 5 хв"
          variant="outlined"
          multiline
          rows={recognizedEvents.length > 0 ? 2 : 6}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          disabled={busy}
          sx={{
            bgcolor: 'background.paper',
            transition: 'all 0.3s ease',
            '& .MuiInputBase-root': { borderRadius: '12px' },
          }}
        />

        {/* Title field */}
        {suggestedTitle !== '' && (
          <TextField
            fullWidth
            label="Назва події"
            value={suggestedTitle}
            onChange={(e) => setSuggestedTitle(e.target.value)}
            sx={{ bgcolor: 'background.paper', '& .MuiInputBase-root': { borderRadius: '12px' } }}
          />
        )}

        {/* Items list */}
        {recognizedEvents.length > 0 ? (
          <Fade in>
            <Box>
              <List disablePadding>
                {recognizedEvents.map((event, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      mb: 1.5,
                      borderRadius: '12px',
                      overflow: 'hidden',
                      borderColor: editingIndex === index ? ACCENT : '#e0e0e0',
                      borderWidth: editingIndex === index ? 2 : 1,
                    }}
                  >
                    <ListItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                          {getEventIcon(event.type)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Назва вправи"
                              value={event.name}
                              onChange={(e) => handleUpdateEventField(index, 'name', e.target.value)}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                {event.name}
                              </Typography>
                              {event.originalText && (
                                <Tooltip title={`Оригінальний текст: "${event.originalText}"`}>
                                  <InfoIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                </Tooltip>
                              )}
                              <Tooltip title={`Час: ${formatTime(event.scheduledLdt)}`}>
                                <AccessTimeIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            variant={editingIndex === index ? 'contained' : 'text'}
                            color={editingIndex === index ? 'warning' : 'inherit'}
                            onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                            startIcon={editingIndex === index ? <SaveIcon /> : <EditIcon />}
                          >
                            {editingIndex === index ? 'Ок' : 'Змінити'}
                          </Button>
                          <IconButton size="small" color="error" onClick={() => handleDeleteItem(index)} sx={{ opacity: 0.7 }}>
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box sx={{ width: '100%', ml: editingIndex === index ? 0 : 5 }}>
                        {editingIndex === index ? (
                          <Grid container spacing={2} sx={{ width: '100%' }}>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                select fullWidth size="small" label="Тип"
                                value={event.type}
                                onChange={(e) => handleUpdateEventField(index, 'type', e.target.value)}
                              >
                                <MenuItem value="strength">Силове</MenuItem>
                                <MenuItem value="cardio">Кардіо</MenuItem>
                                <MenuItem value="stretching">Розтяжка</MenuItem>
                                <MenuItem value="unrecognized">Невідомо</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth size="small" label="Кількість" type="number"
                                value={event.totalAmount || ''}
                                onChange={(e) => handleUpdateEventField(index, 'totalAmount', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                fullWidth size="small" label="Од. виміру"
                                value={event.unit || ''}
                                onChange={(e) => handleUpdateEventField(index, 'unit', e.target.value)}
                              />
                            </Grid>
                            <Grid item xs={6} sm={3}>
                              <TextField
                                select fullWidth size="small" label="Інтенсивність"
                                value={event.intensity || 'medium'}
                                onChange={(e) => handleUpdateEventField(index, 'intensity', e.target.value)}
                              >
                                <MenuItem value="low">Низька</MenuItem>
                                <MenuItem value="medium">Середня</MenuItem>
                                <MenuItem value="high">Висока</MenuItem>
                              </TextField>
                            </Grid>
                            {event.caloriesBurn?.perUnit && (
                              <Grid item xs={12}>
                                <Box sx={{ p: 1, bgcolor: '#FFF3E0', borderRadius: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#E65100' }}>
                                    {`Витрати: ${((event.caloriesBurn.perUnit) * (Number(event.totalAmount) || 0)).toFixed(0)} ккал (${event.caloriesBurn.perUnit.toFixed(1)} ккал/${event.unit || 'од'})`}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            <Chip
                              label={`${event.totalAmount || 0} ${event.unit || ''}`}
                              size="small" variant="outlined" sx={{ fontWeight: 600 }}
                            />
                            <Chip
                              label={INTENSITY_LABELS[event.intensity || 'medium']}
                              size="small"
                              sx={{
                                bgcolor: `${INTENSITY_COLORS[event.intensity || 'medium']}22`,
                                color: INTENSITY_COLORS[event.intensity || 'medium'],
                                fontWeight: 700,
                              }}
                            />
                            {event.caloriesBurn?.perUnit && (
                              <Chip
                                label={`${((event.caloriesBurn.perUnit) * (Number(event.totalAmount) || 0)).toFixed(0)} ккал`}
                                size="small"
                                sx={{ fontWeight: 700, bgcolor: '#FF9800', color: '#fff' }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                  </Paper>
                ))}
              </List>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={() => {
                    setRecognizedEvents([]);
                    setSuggestedTitle('');
                    setStep('input');
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                  sx={{ color: 'text.secondary', textDecoration: 'underline', fontSize: '0.75rem' }}
                >
                  Не те? Уточніть запит
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="text"
                  onClick={handleAddItemManual}
                  sx={{ color: ACCENT, fontWeight: 700 }}
                >
                  Додати вправу вручну
                </Button>
              </Box>
            </Box>
          </Fade>
        ) : (
          step !== 'input' && (
            <Box sx={{ py: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography color="textSecondary">Вправи відсутні. Додайте вручну або натисніть "Розпізнати".</Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                onClick={handleAddItemManual}
                sx={{ color: ACCENT, borderColor: ACCENT }}
              >
                Додати вправу вручну
              </Button>
            </Box>
          )
        )}

        {totalCalories > 0 && (
          <Fade in>
            <Paper sx={{ p: 2, bgcolor: '#FFF3E0', borderRadius: '12px', border: '1px solid #FF9800' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: '#E65100', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                Загальні витрати:
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1 }}>{totalCalories.toFixed(0)}</Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>ккал</Typography>
              </Box>
            </Paper>
          </Fade>
        )}

        <SchedulingBlock
          showDateTimePicker={showDateTimePicker}
          onShowDateTimePickerChange={setShowDateTimePicker}
          scheduledAt={scheduledAt}
          onScheduledAtChange={setScheduledAt}
          isCompleted={isCompleted}
          onIsCompletedChange={setIsCompleted}
          switchLabel="Встановити дату та час"
          switchSize="small"
          gap={1}
        />
      </DialogContent>

      {/* ─── Actions ────────────────────────────────────────────────────── */}
      <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
        {dialogActions}
      </DialogActions>
    </Dialog>
  );
};
